import { useEffect, useState } from "react";
import type { ActiveSession, Profile, ProfileSettings } from "../shared/types";
import { api } from "./api";
import { Auth } from "./Auth";
import { Home } from "./Home";
import { Workout } from "./Workout";
import { History } from "./History";
import { Stats } from "./Stats";
import { Routine } from "./Routine";
import { Settings } from "./Settings";
import { Profiles } from "./Profiles";
import { Shell, type Page } from "./Shell";
import { Card, Logo, Spinner } from "./components";
import { flushQueue, getActiveSession, queuedCount, waitForWorkoutWrites, localSessionRecords } from "./offline";
import { normalizeCachedSettings } from "./settings-cache";
import { shouldRetryQueue } from './sync-policy';

type Boot =
  | { loading: true }
  | { loading: false; needsSetup: boolean; profile: Profile | null };

const settingsKey = (profileId: string) => `hardnup-settings:${profileId}`;

function cachedSettings(profile: Profile): ProfileSettings {
  try {
    const value = localStorage.getItem(settingsKey(profile.id));
    return normalizeCachedSettings(profile, value ? JSON.parse(value) : null);
  } catch {
    return normalizeCachedSettings(profile, null);
  }
}

export function App() {
  const [boot, setBoot] = useState<Boot>({ loading: true });
  const [page, setPage] = useState<Page>("home");
  const [initialSession, setInitialSession] = useState<ActiveSession>();
  const [profileSettings, setProfileSettings] =
    useState<ProfileSettings | null>(null);
  const [offlineCount, setOfflineCount] = useState(0);
  const [syncProblem, setSyncProblem] = useState(false);
  const [reminderProfileMismatch, setReminderProfileMismatch] = useState(false);
  const [summary, setSummary] = useState<{
    elapsedSeconds: number;
    completedSets: number;
    progressionFlags: string[];
  } | null>(null);

  async function applyProfile(profile: Profile) {
    localStorage.setItem("hardnup-cached-profile", JSON.stringify(profile));
    let settings = cachedSettings(profile);
    try {
      settings = await api<ProfileSettings>("/settings");
      localStorage.setItem(settingsKey(profile.id), JSON.stringify(settings));
    } catch {
      // Profile-owned cached settings are the safe offline fallback.
    }
    setProfileSettings(settings);
    if (navigator.onLine) {
      const result = await flushQueue(profile.id);
      setOfflineCount(result.remaining);
      setSyncProblem(Boolean(result.blocked));
    }
    const reminderTarget = new URLSearchParams(location.search).get(
      "reminderTarget",
    );
    if (reminderTarget) {
      try {
        const result = await api<{ matchesCurrent: boolean }>(
          `/reminders/target?token=${encodeURIComponent(reminderTarget)}`,
        );
        setReminderProfileMismatch(!result.matchesCurrent);
        if (result.matchesCurrent) {
          const url = new URL(location.href);
          url.searchParams.delete("reminderTarget");
          url.searchParams.delete("from");
          history.replaceState(
            null,
            "",
            `${url.pathname}${url.search}${url.hash}`,
          );
        }
      } catch {
        setReminderProfileMismatch(true);
      }
    } else setReminderProfileMismatch(false);
    setBoot({ loading: false, needsSetup: false, profile });
  }

  async function restoreOffline() {
    try {
      const cached = localStorage.getItem("hardnup-cached-profile");
      if (!cached) return false;
      const profile = JSON.parse(cached) as Profile;
      const active = await getActiveSession(profile.id);
      if (!active && !(await localSessionRecords(profile.id)).length) return false;
      setProfileSettings(cachedSettings(profile));
      setBoot({ loading: false, needsSetup: false, profile });
      setInitialSession(active??undefined);
      setPage(active ? "workout" : "home");
      return true;
    } catch {
      return false;
    }
  }

  async function initialise() {
    try {
      const { needsSetup } = await api<{ needsSetup: boolean }>(
        "/bootstrap/status",
      );
      if (needsSetup) {
        setBoot({ loading: false, needsSetup: true, profile: null });
        return;
      }
      try {
        await applyProfile(await api<Profile>("/me"));
      } catch {
        if (!navigator.onLine && (await restoreOffline())) return;
        setBoot({ loading: false, needsSetup: false, profile: null });
      }
    } catch {
      if (await restoreOffline()) return;
      setBoot({ loading: false, needsSetup: false, profile: null });
    }
  }

  useEffect(() => {
    void initialise();
  }, []);

  const profileId = boot.loading ? null : (boot.profile?.id ?? null);
  useEffect(() => {
    if (!profileId) {
      setOfflineCount(0);
      setSyncProblem(false);
      return;
    }
    let current = true;
    let syncing=false;
    let retryTimer:ReturnType<typeof setTimeout>|undefined;
    const sync = async () => {
      if(syncing||!current) return;
      syncing=true;
      if(retryTimer) clearTimeout(retryTimer);
      try {
        const result = await flushQueue(profileId);
        if (!current) return;
        setOfflineCount(result.remaining);
        setSyncProblem(Boolean(result.blocked));
        if(shouldRetryQueue(result)) retryTimer=setTimeout(()=>{void sync();},4000);
      } finally {syncing=false;}
    };
    const count = async () => {
      if (current) setOfflineCount(await queuedCount(profileId));
    };
    void count();
    if (navigator.onLine) void sync();
    window.addEventListener("online", sync);
    window.addEventListener('focus',sync);
    const visibility=()=>{if(document.visibilityState==='visible') void sync();};
    document.addEventListener('visibilitychange',visibility);
    return () => {
      current = false;
      if(retryTimer) clearTimeout(retryTimer);
      window.removeEventListener("online", sync);
      window.removeEventListener('focus',sync);
      document.removeEventListener('visibilitychange',visibility);
    };
  }, [profileId,offlineCount]);

  async function updateQueue() {
    if (profileId) setOfflineCount(await queuedCount(profileId));
  }

  async function switchProfile() {
    await waitForWorkoutWrites();
    try {
      await api("/auth/logout", { method: "POST", body: "{}" });
    } catch {
      /* local sign-out still applies */
    }
    localStorage.removeItem("hardnup-cached-profile");
    setProfileSettings(null);
    setInitialSession(undefined);
    setSummary(null);
    setOfflineCount(0);
    setSyncProblem(false);
    setReminderProfileMismatch(false);
    setBoot({ loading: false, needsSetup: false, profile: null });
    setPage("home");
  }

  if (boot.loading)
    return (
      <main className="splash">
        <Logo />
        <Spinner label="Opening your private gym" />
      </main>
    );
  if (!boot.profile)
    return (
      <Auth
        needsSetup={boot.needsSetup}
        onAuthenticated={() => void initialise()}
      />
    );
  if (!profileSettings)
    return (
      <main className="splash">
        <Logo />
        <Spinner label="Loading your workout settings" />
      </main>
    );
  const profile = boot.profile;
  if (reminderProfileMismatch)
    return (
      <main className="auth-page">
        <Logo />
        <Card>
          <p className="eyebrow">Private reminder</p>
          <h1>Switch to the reminder’s profile</h1>
          <p>
            This reminder belongs to a different private profile on this iPhone.
            Switch profile, then unlock the correct one to open it.
          </p>
          <button
            className="button button--primary button--full"
            onClick={() => void switchProfile()}
          >
            Switch profile securely
          </button>
        </Card>
      </main>
    );

  function content() {
    if (summary)
      return (
        <Completion
          summary={summary}
          onDone={() => {
            setSummary(null);
            setPage("home");
          }}
        />
      );
    switch (page) {
      case "home":
        return (
          <Home profileId={profile.id} timezone={profile.timezone}
            onWorkout={(session) => {
              setInitialSession(session);
              setPage("workout");
            }}
          />
        );
      case "workout":
        return (
          <Workout
            key={`${profile.id}:${initialSession?.id??'foreground'}`}
            profileId={profile.id}
            settings={profileSettings!}
            initial={initialSession}
            onExit={() => { setInitialSession(undefined); setPage("home"); }}
            onCompleted={setSummary}
            onQueueChange={() => void updateQueue()}
          />
        );
      case "history":
        return <History profileId={profile.id} onWorkout={(session) => {setInitialSession(session);setPage("workout");}} />;
      case "stats":
        return <Stats />;
      case "routine":
        return <Routine profile={profile} />;
      case "settings":
        return (
          <Settings
            profile={profile}
            initialSettings={profileSettings!}
            onSettingsSaved={(settings) => {
              setProfileSettings(settings);
              localStorage.setItem(
                settingsKey(profile.id),
                JSON.stringify(settings),
              );
            }}
            onProfiles={() => setPage("profiles")}
          />
        );
      case "profiles":
        return <Profiles current={profile} />;
    }
  }

  const focusKey = summary ? "completion" : page;
  const mainLabel = summary
    ? "Workout complete"
    : page === "workout"
      ? "Active workout"
      : page === "profiles"
        ? "Profile management"
        : `${page[0].toUpperCase()}${page.slice(1)} page`;
  return (
    <Shell
      profile={profile}
      page={page}
      focusKey={focusKey}
      mainLabel={mainLabel}
      onNavigate={async (next) => {
        await waitForWorkoutWrites();
        setSummary(null);
        setInitialSession(undefined);
        setPage(next);
      }}
      onSwitchProfile={switchProfile}
      offlineCount={offlineCount}
      syncProblem={syncProblem}
    >
      {content()}
    </Shell>
  );
}

function Completion({
  summary,
  onDone,
}: {
  summary: {
    elapsedSeconds: number;
    completedSets: number;
    progressionFlags: string[];
  };
  onDone: () => void;
}) {
  return (
    <div className="completion">
      <div className="completion__check" aria-hidden="true">
        ✓
      </div>
      <p className="eyebrow">Workout complete</p>
      <h1>Strong work. Logged.</h1>
      <div className="completion-stats">
        <div>
          <strong>
            {Math.floor(summary.elapsedSeconds / 60)}:
            {String(summary.elapsedSeconds % 60).padStart(2, "0")}
          </strong>
          <span>duration</span>
        </div>
        <div>
          <strong>{summary.completedSets}</strong>
          <span>sets</span>
        </div>
        <div>
          <strong>{summary.progressionFlags.length}</strong>
          <span>progression flags</span>
        </div>
      </div>
      {summary.progressionFlags.length > 0 && (
        <Card>
          <h2>Progress these next time</h2>
          <p className="muted">
            Add one load increment where available. For bodyweight work, add
            load or choose a controlled harder variation.
          </p>
          <ul>
            {summary.progressionFlags.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </Card>
      )}
      <p>Your permanent history and next workout rotation are updated.</p>
      <button
        className="button button--primary button--large button--full"
        onClick={onDone}
      >
        Done
      </button>
    </div>
  );
}
